import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';

import './AccountPlans.scss';
import { StripePricingPlan, StripeCheckoutSessionResponse } from '../../../models/Stripe';
import { BillingAccount } from '../../../models/BillingAccount';
import { graphqlSchema } from '../../../services/graphql.schema';
import { numberToCurrencyString } from '../../../services/formatting';
import Button from '../../Forms/Button';
import sharedToasterSubject from '../../../services/shared.toasterSubject';
import { ReactComponent as StarIcon } from './starIcon.svg';
import GraphQlClient from '../../../services/graphql';
import AccountPlansConfirm from './AccountPlans.confirm';

interface AccountPlansProps {
    pricingPlans: StripePricingPlan[];
    isClusterAccount: boolean;
    billingAccount: BillingAccount | null;
}

const DEFAULT_VISIBLE_COUNT = 3;

function AccountPlans({
    pricingPlans,
    isClusterAccount,
    billingAccount
}: AccountPlansProps) {
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
    const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
    const [showAll, setShowAll] = useState(false);

    const [requestCheckoutSession, { loading: checkoutLoading }] = useMutation(graphqlSchema.STRIPE.PRICES.createCheckoutSession, {
        onCompleted: ({ createCheckoutSession }: { createCheckoutSession: StripeCheckoutSessionResponse }) => {
            console.log('Checkout Session Created: ', createCheckoutSession);
            if (createCheckoutSession?.url) {
                window.location.href = createCheckoutSession.url;
            }
        },
        onError: (err) => {
            console.error('Error creating checkout session:', err.message);
            sharedToasterSubject.next({ type: 'error', message: 'Error al iniciar el proceso de pago. Por favor intenta de nuevo o contacta al soporte.' });
        }
    })

    const [changePlan, { loading: changePlanLoading }] = useMutation(graphqlSchema.STRIPE.PRICES.changeSubscriptionPlan, {
        onCompleted: ({ changeSubscriptionPlan }: { changeSubscriptionPlan: boolean }) => {
            if (changeSubscriptionPlan) {
                sharedToasterSubject.next({ type: 'confirm', message: 'Plan cambiado exitosamente.' });
                GraphQlClient.recallCurrentUser();
            } else {
                sharedToasterSubject.next({ type: 'error', message: 'No se pudo cambiar el plan. Por favor intenta de nuevo o contacta al soporte.' });
            }
        },
        onError: (err) => {
            console.error('Error changing subscription plan:', err.message);
            sharedToasterSubject.next({ type: 'error', message: 'Error al cambiar de plan. Por favor intenta de nuevo o contacta al soporte.' });
        }
    })

    const activePriceId = billingAccount?.hasStripeSubscription
        ? billingAccount.stripeSubscriptionCfdiUsage.priceId
        : null;

    const isMaxedOut = billingAccount?.hasStripeSubscription
        ? billingAccount.stripeSubscriptionMaxedOut
        : false;

    const availablePlans = useMemo(() => {
        const sorted = [...pricingPlans].sort((a, b) => a.maxUsage - b.maxUsage);
        if (!activePriceId) return sorted;
        const activePlan = sorted.find(p => p.id === activePriceId);
        if (!activePlan) return sorted;
        return sorted.filter(p => p.maxUsage >= activePlan.maxUsage);
    }, [pricingPlans, activePriceId]);

    const recommendedPlanId = useMemo(() => {
        if (!isMaxedOut || !activePriceId) return null;
        const activeIndex = availablePlans.findIndex(p => p.id === activePriceId);
        if (activeIndex < 0 || activeIndex + 1 >= availablePlans.length) return null;
        return availablePlans[activeIndex + 1].id;
    }, [isMaxedOut, activePriceId, availablePlans]);

    const visiblePlans = useMemo(() => {
        if (showAll) return availablePlans;
        return availablePlans.slice(0, DEFAULT_VISIBLE_COUNT);
    }, [availablePlans, showAll]);

    const handleSelectPlan = () => {
        if (!selectedPlanId) return;
        if (checkoutLoading) return;
        if (changePlanLoading) return;

        if (billingAccount?.hasStripeSubscription) {
            // IF active subs, show confirm dialog first
            setPendingPlanId(selectedPlanId);
            return;
        }
        // IF no active subscription, use checkout
        requestCheckoutSession({
            variables: { pricingId: selectedPlanId }
        });
    };

    const handleConfirmPlanChange = () => {
        if (!pendingPlanId) return;
        changePlan({ variables: { pricingId: pendingPlanId } });
        setPendingPlanId(null);
    };

    if (isClusterAccount) return null;

    const confirmFromPlan = pendingPlanId ? pricingPlans.find(p => p.id === activePriceId) : null;
    const confirmToPlan = pendingPlanId ? pricingPlans.find(p => p.id === pendingPlanId) : null;

    return (
        <div className='planSelectSection card'>
            <h3 className='title'>Planes de pago</h3>
            <p className='subtitle'>
                {
                    billingAccount?.hasStripeSubscription ?
                    'Al cambiar de plan solo pagarás la diferencia entre tu plan actual y el nuevo plan seleccionado.'
                    : 'Accede a precios de facturación reducidos eligiendo un plan mensual controlado basado en tu consumo estimado.'
                }
            </p>
            <div className='plansContainer'>
                <div className='plansList'>
                    {visiblePlans.map((plan) => {
                        const isActive = plan.id === activePriceId;
                        const isSelected = plan.id === selectedPlanId;
                        const isRecommended = plan.id === recommendedPlanId;
                        const isAboveThousand = plan.maxUsage >= 1000;
                        const className = [
                            'planEntry',
                            isActive ? 'active' : '',
                            isSelected ? 'selected' : '',
                            isRecommended ? 'recommended' : '',
                            isAboveThousand && !recommendedPlanId ? 'aboveThousand' : '',
                        ].filter(Boolean).join(' ');

                        return (
                            <div
                                key={plan.id}
                                className={className}
                                onClick={() => !isActive && setSelectedPlanId(plan.id)}
                            >
                                <div className={`planInfo`}>
                                    <p className='planName'>{plan.name}</p>
                                    <p className='planPrice'>
                                        ${numberToCurrencyString(plan.unitAmountCents / 100)} MXN
                                    </p>
                                    {
                                        plan.isMeteredBilling ?
                                            <p className='planCfdiCost'>
                                                Hasta ${numberToCurrencyString(plan.cfdiMinAmountCents / 100)} MXN por factura <br />
                                                Factura adicional a $0.60 MXN

                                            </p>
                                            :
                                            <p className='planCfdiCost'>
                                                Hasta ${numberToCurrencyString(plan.cfdiMinAmountCents / 100)} MXN por factura
                                            </p>
                                    }
                                </div>
                                <div className='planUsage'>
                                    {
                                        isActive && <span className={`chip ${isActive ? 'active' : 'hidden'}`}>Activo</span>
                                    }
                                    {
                                        isRecommended && <span className={`chip ${isRecommended ? 'recommended' : 'hidden'}`}>
                                            Sugerido
                                            <StarIcon />
                                        </span>
                                    }
                                    {
                                        !isActive && !isRecommended && <span className={`chip 'hidden'`}></span>
                                    }
                                    <p className='maxUsage'>{plan.maxUsage.toLocaleString()} facturas</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {availablePlans.length > DEFAULT_VISIBLE_COUNT && (
                    <Button tertiary size='small' handleClick={() => setShowAll(!showAll)}>
                        <span>{showAll ? 'Ver menos planes' : 'Ver todos los planes disponibles'}</span>
                    </Button>
                )}
            </div>
            <div className='planAction'>
                <Button
                    primary
                    size='small'
                    disabled={!selectedPlanId || checkoutLoading || changePlanLoading}
                    handleClick={handleSelectPlan}
                >
                    <span>{checkoutLoading || changePlanLoading ? 'Procesando...' : 'Seleccionar Plan'}</span>
                </Button>
            </div>
            {!!pendingPlanId && billingAccount?.hasStripeSubscription && confirmFromPlan && confirmToPlan && (
                <AccountPlansConfirm
                    isOpen={true}
                    fromPlan={confirmFromPlan}
                    toPlan={confirmToPlan}
                    billingAccount={billingAccount}
                    onCancel={() => setPendingPlanId(null)}
                    onConfirm={handleConfirmPlanChange}
                />
            )}
        </div>
    );
}

export default AccountPlans;